import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    Input,
    OnInit,
    Output,
    Renderer2,
    ViewEncapsulation,
} from '@angular/core';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import { FiAttachmentDisplaySize, FiImageSize, FileType } from '@fi-shared/types';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { MediaBankHelperService } from '@fi-shared/media-bank/media-bank-helper.service';
import { ITextEditorLinkAttachment } from '@fi-shared/fi-text-editor/fi-text-editor';
import { TextEditorAttachmentType } from '@fi-shared/fi-text-editor/fi-text-editor-attachment/text-editor-attachment-type';
import { TextEditorService } from '@fi-shared/fi-text-editor/text-editor.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { isDefined } from '@fi-shared/utils';
import { MediaViewerService } from '@fi-shared/media-viewer/media-viewer.service';
import { FiVimeoVideoStatus } from '@fi-shared/media-bank/fi-vimeo/fi-vimeo-data.service';
import { VimeoUnavailablePlaceholderSize } from '../../fi-vimeo/fi-vimeo-enum';

export enum FiAttachmentNameMaxSize {
    Thumbnail = 8,
    AttachmentPreview = 13,
    Default = 40,
}

@Component({
    selector: 'fi-attachments-list-item',
    templateUrl: './fi-attachments-list-item.component.html',
    styleUrls: ['./fi-attachments-list-item.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-attachments-list-item' },
    standalone: false,
})
export class FiAttachmentsListItemComponent implements OnInit {
    @Input() mediaBankFileReference: IMediaBankFileReference;
    @Input() mediaBankFileReferences: IMediaBankFileReference[];
    @Input() linkAttachment: ITextEditorLinkAttachment;
    @Input() showRemoveButton: boolean;
    @Input() showEditButton: boolean;
    @Input() showDownloadButton: boolean;
    @Input() imageSize: FiImageSize;
    @Input() viewSize: FiImageSize;
    @Input() popupImageSize = FiImageSize.Large;
    @Input() isImgDraggable: boolean = true;
    @Input() isVimeoPreviewActionButtonsShown: boolean = true;
    @Input() disableMediaViewerDialog: boolean = false;
    @Input() isNewLayout: boolean;
    @Input() isPdfFilePreviewMode: boolean;
    @Input() isLinkImagePreviewDisabled: boolean;
    @Input() preventDownloadFile: boolean;
    @Input() vimeoPreviewCovered: boolean = false;
    @Input() vimeoPlaceholderSize: VimeoUnavailablePlaceholderSize = VimeoUnavailablePlaceholderSize.Small;
    @Output() removeClick = new EventEmitter<MouseEvent>();
    @Output() editClick = new EventEmitter<MouseEvent>();
    @Output() onDownloadFileButtonClick = new EventEmitter<void>();

    @Input() isCustomPreviewHandler: boolean;
    @Output() onAttachmentClick = new EventEmitter<IMediaBankFileReference>();

    @HostBinding('class.new-layout') get _isNewLayout() {
        return this.isNewLayout;
    }

    @HostBinding('class.embed-video-attachment') protected get isYoutubeLinkAttachment(): boolean {
        return this.linkAttachment?.LinkType === TextEditorAttachmentType.Youtube;
    }
    @HostBinding('class.file-attachment') protected get isFileAttachment(): boolean {
        return this.isDownloadableFile();
    }
    @HostBinding('class') @Input() displaySize: FiAttachmentDisplaySize;

    protected fileUrl: string;
    protected fileSafeUrl: SafeResourceUrl;

    protected imageUrl: string;

    private popupImageUrl: string;

    protected readonly FileType = FileType;
    protected readonly TextEditorAttachmentType = TextEditorAttachmentType;
    private maxFileNameLength: number;
    private readonly imageLoadedClass = 'loaded';

    constructor(
        private mediaStorageService: FiMediaStorageService,
        private loadingService: LoadingService,
        private mediaBankHelperService: MediaBankHelperService,
        private textEditorService: TextEditorService,
        private sanitizer: DomSanitizer,
        private elementRef: ElementRef<HTMLElement>,
        private renderer: Renderer2,
        private mediaViewerService: MediaViewerService,
    ) {}

    ngOnInit(): void {
        this.maxFileNameLength = this.getMaxFileNameLength();

        this.setUrls();

        if (!isDefined(this.viewSize)) {
            this.viewSize = this.imageSize;
        }

        if (isDefined(this.viewSize)) {
            this.elementRef.nativeElement.classList.add('size-' + FiImageSize[this.viewSize]);
        }
    }

    setUrls() {
        if (this.mediaBankFileReference) {
            this.fileUrl = this.mediaStorageService.getMediaFileUrl(this.mediaBankFileReference);
            this.popupImageUrl = this.mediaStorageService.getMediaFileUrl(this.mediaBankFileReference, {
                size: this.popupImageSize,
                contentDisposition: 'inline',
                alwaysUseDownloadFromObjectStorageUrl: this.mediaBankFileReference.FileType === FileType.Image,
            });

            const imageSizeForImage = this.imageSize || FiImageSize.Medium;

            this.imageUrl = this.mediaStorageService.getMediaFileUrl(this.mediaBankFileReference, {
                size: imageSizeForImage,
            });
        } else if (this.linkAttachment) {
            if (this.isYoutubeLinkAttachment) {
                this.fileUrl = this.textEditorService.getEmbdedVideoUrl(this.linkAttachment.Url);
                this.fileSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.fileUrl);
            } else if (this.isImageOrGifLinkAttachment()) {
                this.imageUrl = this.linkAttachment.Url;
            }
            // todo implement other link types
        }
    }

    private readonly attachmentItemSizeVimeoPreviewStatusClass = 'attachment-item-size-vimeo-preview-status';
    protected onVimeoStatusChange(vimeoVideoStatus: FiVimeoVideoStatus): void {
        if (vimeoVideoStatus === FiVimeoVideoStatus.Ok) {
            this.elementRef.nativeElement.classList.remove(this.attachmentItemSizeVimeoPreviewStatusClass);
        } else {
            this.elementRef.nativeElement.classList.add(this.attachmentItemSizeVimeoPreviewStatusClass);
        }
    }

    public openPdfFile(): void {
        this.mediaBankHelperService.downloadMediaBankFile(this.mediaBankFileReference);
    }

    protected isImageOrGifLinkAttachment(): boolean {
        return (
            this.linkAttachment.LinkType === TextEditorAttachmentType.Image ||
            this.linkAttachment.LinkType === TextEditorAttachmentType.Gif
        );
    }

    protected openMediaViewerDialog(): void {
        if (this.disableMediaViewerDialog) {
            return;
        }

        if (this.isCustomPreviewHandler) {
            this.onAttachmentClick.emit(this.mediaBankFileReference);
            return;
        }

        this.mediaViewerService.Show(this.popupImageUrl, undefined, this.mediaBankFileReferences);
    }

    protected isVimeoVideo(): boolean {
        return (
            this.mediaBankFileReference &&
            this.mediaStorageService.isVimeoStorage(this.mediaBankFileReference.StorageType)
        );
    }

    protected isNativeVideo(): boolean {
        return (
            this.mediaBankFileReference &&
            this.mediaBankFileReference.FileType === FileType.Movie &&
            !this.isVimeoVideo()
        );
    }

    protected isImage(): boolean {
        return this.mediaBankFileReference && this.mediaBankFileReference.FileType === FileType.Image;
    }

    protected isPdfFileInPreviewMode(): boolean {
        return this.mediaBankFileReference.FileType === FileType.Pdf && this.isPdfFilePreviewMode;
    }

    protected isDownloadableFile(): boolean {
        return !this.isVimeoVideo() && !this.isNativeVideo() && !this.isImage();
    }

    protected onRemoveClick($event: MouseEvent) {
        this.removeClick.emit($event);
    }

    protected onEditClick($event: MouseEvent) {
        this.editClick.emit($event);
    }

    protected downloadMediaBankFile($event: MouseEvent) {
        $event.preventDefault();
        $event.stopPropagation();

        this.onDownloadFileButtonClick.emit();
        if (this.preventDownloadFile) {
            return;
        }

        // create media bank file from reference
        this.loadingService.trackPromise(
            this.mediaBankHelperService.downloadMediaBankFileRef(this.mediaBankFileReference, {
                openPdfInNewWindow: true,
            }),
        );
    }

    protected getTruncatedName(): string {
        return this.truncate(
            this.mediaBankFileReference.Name + this.mediaBankFileReference.Extension,
            this.maxFileNameLength,
        );
    }

    protected getTitleForName(): string {
        return this.shouldTruncate(
            this.mediaBankFileReference.Name + this.mediaBankFileReference.Extension,
            this.maxFileNameLength,
        )
            ? this.mediaBankFileReference.Name + this.mediaBankFileReference.Extension
            : undefined;
    }

    public onImageLoad(imageElement: HTMLImageElement): void {
        this.renderer.addClass(imageElement, this.imageLoadedClass);
    }

    protected getPdfPreviewUrl(): string {
        return this.mediaStorageService.getMediaFileUrl(this.mediaBankFileReference);
    }

    /* isImageSizeSet(): boolean {
        return this.imageSize !== FiImageSize.Document && this.mediaBankFileReference && this.isImage();
    }*/

    private shouldTruncate(n: string, len: number): boolean {
        const ext = n.substring(n.lastIndexOf('.') + 1, n.length).toLowerCase();
        let filename = n.replace('.' + ext, '');
        return !(filename.length <= len);
    }

    private truncate(n: string, len: number): string {
        const ext = n.substring(n.lastIndexOf('.') + 1, n.length).toLowerCase();
        let filename = n.replace('.' + ext, '');
        if (filename.length <= len) {
            return n;
        }
        filename = filename.substr(0, len) + (n.length > len ? '... ' : '');
        return filename + '.' + ext;
    }

    private getMaxFileNameLength(): number {
        if (this.imageSize === FiImageSize.Thumbnail) return FiAttachmentNameMaxSize.Thumbnail;
        if (this.imageSize === FiImageSize.Small) return FiAttachmentNameMaxSize.AttachmentPreview;
        return FiAttachmentNameMaxSize.Default;
    }
}
